import { BusinessObjectBase } from './base';
import { supportAttachmentSchema } from '@schemas/business-objects/support-attachment';
import type { Account } from './account';
import type { SupportCase } from './support-case';
import type { SupportComment } from './support-comment';
import type { User } from './user';
import type { Id } from '../base-types';

export interface SupportAttachmentData {
  readonly id: number;
  readonly fileName?: string;
  readonly mimeType?: string;
  readonly base64Content?: string;
  readonly caseId: number;
  readonly case?: SupportCase;
  readonly accountId: number;
  readonly account?: Account;
  readonly commentId: number;
  readonly comment?: SupportComment;
  readonly userId: Id;
  readonly user?: User;
  readonly createdAt?: Date;
  readonly createdById?: Id;
  readonly updatedAt?: Date;
  readonly updatedById?: Id;
  readonly createdBy?: User;
  readonly updatedBy?: User;
}

export interface SupportAttachment extends Readonly<SupportAttachmentData> {}
type SupportAttachmentCreateWithChildrenData = Partial<SupportAttachmentData>;

export class SupportAttachment extends BusinessObjectBase {
  static readonly entityName = 'SupportAttachment' as const;
  static readonly plural = 'support-attachments';
  static schema = supportAttachmentSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<SupportAttachment[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new SupportAttachment(data));
  }

  static async findOne(filter?: any): Promise<SupportAttachment | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new SupportAttachment(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<SupportAttachment> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new SupportAttachment(data);
  }

  static async create(
    data: Omit<SupportAttachmentData, 'id'>,
  ): Promise<SupportAttachment> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new SupportAttachment(result);
  }

  static async createMany(
    data: Omit<SupportAttachmentData, 'id'>[],
  ): Promise<SupportAttachment[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new SupportAttachment(item));
  }

  static async createWithChildren(
    data: SupportAttachmentCreateWithChildrenData,
  ): Promise<SupportAttachment> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new SupportAttachment(result);
  }

  static async createManyWithChildren(
    data: SupportAttachmentCreateWithChildrenData[],
  ): Promise<SupportAttachment[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new SupportAttachment(item));
  }

  static async updateById(
    id: number,
    updates: Partial<SupportAttachmentData>,
  ): Promise<SupportAttachment> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new SupportAttachment(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
